import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Check, Circle, CheckCircle, CircleNotch, Copy } from "@phosphor-icons/react";
import MonacoEditor from "../../components/MonacoEditor";
import PracticeTitleContent from "../../components/PracticeTitleContent";
import InstructorReveal from "../../components/InstructorReveal";
import type { AnswerResultDto, QuizQuestionDto } from "./quizApi";
import { runJsTests, type TestRunResult } from "./jsTestRunner";

const DIFFICULTY_LABEL: Record<number, string> = { 1: "Kolay", 2: "Orta", 3: "Zor" };

/**
 * Soru numarasi: ogrenci bir soruda sorun gorurse egitmene bu numarayla bildirir.
 * Tiklayinca panoya kopyalanir.
 */
function QuestionIdChip({ id }: { id: number }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);

  return (
    <button
      type="button"
      title="Bu soruyla ilgili bir sorun bildirirken bu numarayı yaz"
      onClick={() => {
        navigator.clipboard
          ?.writeText(`Soru #${id}`)
          .then(() => setCopied(true))
          .catch(() => {});
      }}
      className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/5 hover:bg-white/10 text-white/50 hover:text-white/80 transition-colors tabular-nums"
    >
      {copied ? <Check size={12} weight="bold" /> : <Copy size={12} />}
      {copied ? "Kopyalandı" : `Soru #${id}`}
    </button>
  );
}

export default function QuizQuestion({
  question,
  index,
  total,
  result,
  isSubmitting,
  onSubmit,
  onNext,
  preview = false,
}: {
  question: QuizQuestionDto;
  index: number;
  total: number;
  result: AnswerResultDto | null;
  isSubmitting: boolean;
  onSubmit: (
    answer: string,
    clientTestResult?: { passed: number; total: number; kind?: "structural" | "assertion" },
  ) => void;
  onNext: () => void;
  /** Egitmenin tek soru onizlemesi: puan kaydedilmez, "sonraki" yerine bastan cozulur. */
  preview?: boolean;
}) {
  const [code, setCode] = useState(question.description.preText ?? "");
  const [selected, setSelected] = useState<string>("");
  const [testRun, setTestRun] = useState<TestRunResult | null>(null);
  const [running, setRunning] = useState(false);

  // Soru degistiginde editoru ve secimi sifirla
  useEffect(() => {
    setCode(question.description.preText ?? "");
    setSelected("");
    setTestRun(null);
  }, [question.id, question.description.preText]);

  const isTest = question.type === "TEST";
  // Dogru cevaplanana kadar kilitlenmez: yanlissa tekrar denenebilir.
  // Puan ILK denemeye gore kaydedilir (sunucu), yani tekrar deneme ustaligi sismez.
  const locked = result !== null && result.isCorrect;
  const answered = result !== null;

  // Monaco'nun tanidigi diller. Eskiden yalnizca css/html geciyordu, java/sql/
  // python JAVASCRIPT olarak renklendiriliyordu — Java kodu bozuk gorunuyordu.
  const language = useMemo(() => {
    const lang = question.description.lang;
    // `properties` Monaco'da ayri bir dil degil; `ini` modu .properties'i karsiliyor.
    if (lang === "properties") return "ini";
    const supported = ["javascript", "css", "html", "java", "sql", "python"];
    return lang && supported.includes(lang) ? lang : "javascript";
  }, [question.description.lang]);

  const handleSubmit = async () => {
    if (isTest) {
      if (!selected) return;
      onSubmit(selected);
      return;
    }
    // Gizli test varsa once tarayicida calistir, sonucu cevapla birlikte gonder
    if (question.tests?.harness === "js") {
      setRunning(true);
      const run = await runJsTests(code, question.tests.entry, question.tests.cases);
      setRunning(false);
      setTestRun(run);
      // index -1 = kodun kendisi calismadi (fonksiyon yok, sozdizimi, timeout).
      // Bu durumda "kenar durumlarini kontrol et" demek yaniltici olur.
      const structural = run.failures.some((f) => f.index < 0);
      onSubmit(code, {
        passed: run.passed,
        total: run.total,
        kind: structural ? "structural" : "assertion",
      });
      return;
    }
    onSubmit(code);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 text-xs text-white/50">
        <span className="font-semibold text-white/80">
          Soru {index + 1} / {total}
        </span>
        <span className="px-2 py-0.5 rounded-full bg-myGb/50">
          {DIFFICULTY_LABEL[question.difficulty]}
        </span>
        {/* Konu adi bilerek GIZLI: dogru araci ogrencinin kendisi hatirlamali.
            Cevaptan sonra ozet ekraninda gosteriliyor. */}
        {question.tests && (
          <span className="px-2 py-0.5 rounded-full bg-myGb/50">Gizli testlerle ölçülür</span>
        )}
        <QuestionIdChip id={question.id} />
      </div>

      <div className="bg-myGb/10 text-white/90 rounded-2xl p-6 practiceTitle">
        <PracticeTitleContent title={question.description.title} />
      </div>

      {isTest ? (
        <div className="flex flex-col gap-2">
          {(question.description.options ?? []).map((opt, i) => {
            const value = String(i + 1); // secenekler 1-indexed
            const isSelected = selected === value;
            // Secim sirasinda YESIL kullanilmaz — yesil "dogru" demek.
            // Cevaplandiktan sonra secilen sik dogruysa yesil, yanlissa kirmizi.
            // Dogru sik yanlis cevapta ele VERILMEZ (ogrenci tekrar deneyecek).
            // Egitmen dogru sikki bastan gorur (mevcut Selectable'daki
            // `isCorrect && isRevealed` davranisinin karsiligi).
            const isCorrectOption = question.instructorAnswer === value;
            const state = isCorrectOption
              ? "dogru"
              : !answered
                ? isSelected
                  ? "secili"
                  : "bos"
                : isSelected
                  ? result!.isCorrect
                    ? "dogru"
                    : "yanlis"
                  : "bos";
            const Icon = isSelected ? CheckCircle : Circle;
            return (
              <button
                key={value}
                type="button"
                disabled={locked}
                onClick={() => setSelected(value)}
                className={clsx(
                  "text-left px-4 py-3 rounded-xl border transition flex items-start gap-2.5",
                  state === "bos" && "border-white/15 bg-myGb/25",
                  state === "secili" && "border-white/50 bg-white/[0.07]",
                  state === "dogru" && "border-myGreen bg-myGreen/15",
                  state === "yanlis" && "border-myRed bg-myRed/15",
                  !locked && "hover:border-white/40 cursor-pointer",
                  locked && "cursor-default",
                )}
              >
                <Icon
                  size={18}
                  weight={isSelected ? "fill" : "regular"}
                  className={clsx(
                    "shrink-0 mt-0.5",
                    state === "dogru" && "text-myGreen",
                    state === "yanlis" && "text-myRed",
                    (state === "bos" || state === "secili") && "text-white/50",
                  )}
                />
                <span dangerouslySetInnerHTML={{ __html: opt }} />
              </button>
            );
          })}
        </div>
      ) : (
        <div className="h-[340px] rounded-xl overflow-hidden border border-white/10">
          <MonacoEditor
            value={code}
            handleChange={setCode}
            language={language}
            options={{ readOnly: locked }}
          />
        </div>
      )}

      {testRun && (
        <div
          className={clsx(
            "rounded-xl px-4 py-3 text-sm",
            testRun.passed === testRun.total ? "bg-myGreen/10 text-myGreen" : "bg-myRed/10 text-white/80",
          )}
        >
          {testRun.passed}/{testRun.total} test geçti
          {testRun.failures.length > 0 && (
            <ul className="mt-1 list-disc list-inside text-white/60 text-xs">
              {testRun.failures.slice(0, 3).map((f, i) => (
                <li key={i}>
                  {f.index >= 0 ? `${f.index + 1}. durum: ` : ""}
                  {f.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {result && (
        <div
          className={clsx(
            "rounded-xl px-4 py-3 text-sm whitespace-pre-wrap",
            result.isCorrect ? "bg-myGreen/10 text-myGreen" : "bg-myGb/40 text-white/80",
          )}
        >
          {result.feedback}
        </div>
      )}

      {/* Egitmene beklenen cevap. Ogrenci yanitinda `instructorAnswer` alani
          HIC bulunmadigi icin bu blok ogrenciye asla render olmaz. */}
      {question.instructorAnswer && (
        <InstructorReveal>
          <pre className="text-xs whitespace-pre-wrap font-mono text-white/80">
            {isTest
              ? `${question.instructorAnswer}. şık`
              : question.instructorAnswer}
          </pre>
          {question.instructorCases && question.instructorCases.length > 0 && (
            <div className="mt-3 text-xs text-white/50">
              <div className="font-semibold mb-1">Gizli testler</div>
              <ul className="flex flex-col gap-0.5">
                {question.instructorCases.map((c, i) => (
                  <li key={i} className="font-mono">
                    {question.tests?.entry}({JSON.stringify(c.args).slice(1, -1)}) ={" "}
                    {JSON.stringify(c.expected)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </InstructorReveal>
      )}

      <div className="flex gap-3 items-center">
        {answered && !result!.isCorrect && !preview && (
          <span className="text-xs text-white/40">
            Puanın ilk denemene göre kaydedildi; tekrar denemek serbest.
          </span>
        )}

        {/* Yanlissa kilitlenmez — ogrenci tekrar deneyebilir. Sunucu puani ILK
            denemeden sabitliyor, yani tekrar deneme ustaligi sismiyor. */}
        {!locked && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || running || (isTest && !selected)}
            className="practice-button bg-myGreen/80 border border-myGreen text-black/80 ml-auto hover:bg-myGreen disabled:opacity-40"
          >
            {isSubmitting || running ? (
              <CircleNotch className="animate-spin" />
            ) : answered ? (
              "Tekrar dene"
            ) : (
              "Gönder"
            )}
          </button>
        )}

        {/* Cozmeden gecilemez: "Sonraki soru" yalnizca DOGRU cevapta cikar.
            3 basarisiz denemeden sonra soluk bir "gec" baglantisi acilir —
            sorularin ~%92'si AI ile puanlaniyor ve AI yanilabilir; ogrenci
            kalici olarak kilitli kalmasin. */}
        {locked ? (
          <button
            type="button"
            onClick={onNext}
            className="practice-button bg-myGreen/80 border border-myGreen text-black/80 ml-auto hover:bg-myGreen"
          >
            {preview ? "Baştan çöz" : index + 1 === total ? "Bitir" : "Sonraki soru"}
          </button>
        ) : (
          answered &&
          result!.attempt >= 3 && (
            <button
              type="button"
              onClick={onNext}
              className="text-xs text-white/35 hover:text-white/60 underline underline-offset-2 transition"
            >
              {index + 1 === total ? "Bitir" : "Bu soruyu geç"}
            </button>
          )
        )}
      </div>
    </div>
  );
}
